import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Table,
  Badge,
  Form,
  InputGroup,
  Row,
  Col,
  Spinner,
  Pagination
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import axios from 'helpers/api';
import paths from 'routes/paths';
import { toast } from 'react-toastify';
import { createIframeReportBlobUrl } from 'helpers/iframeReportPreview';

const SERVER_TIMEZONE = 'America/New_York';

const BUCKET_CONFIG = {
  ALL: {
    label: 'All Unverified',
    color: '#e63757'
  },
  within_7: {
    label: 'Unverified within 7 days (≤ 7d)',
    color: '#2c7be5'
  },
  days_8_14: {
    label: 'Unverified within 8–14 days',
    color: '#f5803e'
  },
  days_15_21: {
    label: 'Unverified within 15–21 days',
    color: '#ea580c'
  },
  over_21: {
    label: 'Unverified > 21 days',
    color: '#e63757'
  }
};

const calculateDiffDays = createdDateStr => {
  if (!createdDateStr) return null;

  let createdMoment;
  if (
    typeof createdDateStr === 'string' &&
    (createdDateStr.includes('T') || createdDateStr.includes('Z'))
  ) {
    createdMoment = moment(createdDateStr).tz(SERVER_TIMEZONE).startOf('day');
  } else {
    createdMoment = moment.tz(
      createdDateStr,
      [
        'MM/DD/YYYY',
        'MM/DD/YYYY HH:mm:ss',
        'MM/DD/YYYY hh:mm:ss A',
        'MM/DD/YYYY hh:mm A',
        'YYYY-MM-DD',
        'YYYY-MM-DD HH:mm:ss',
        'MM-DD-YYYY',
        'YYYY/MM/DD',
        moment.ISO_8601
      ],
      SERVER_TIMEZONE
    ).startOf('day');
  }

  if (!createdMoment || !createdMoment.isValid()) {
    createdMoment = moment(createdDateStr).tz(SERVER_TIMEZONE).startOf('day');
  }

  if (!createdMoment.isValid()) return null;

  const today = moment.tz(SERVER_TIMEZONE).startOf('day');
  const diff = today.diff(createdMoment, 'days');
  return diff >= 0 ? diff : 0;
};

const matchesBucket = (diffDays, bucketKey) => {
  if (!bucketKey || bucketKey === 'ALL') return true;
  if (diffDays === null || diffDays === undefined) return false;
  if (bucketKey === 'within_7') return diffDays <= 7;
  if (bucketKey === 'days_8_14') return diffDays >= 8 && diffDays <= 14;
  if (bucketKey === 'days_15_21') return diffDays >= 15 && diffDays <= 21;
  if (bucketKey === 'over_21') return diffDays > 21;
  return true;
};

const getAgeBadgeColor = diffDays => {
  if (diffDays === null || diffDays === undefined) return '#9da9bb';
  if (diffDays <= 7) return '#2c7be5';
  if (diffDays <= 14) return '#f5803e';
  if (diffDays <= 21) return '#ea580c';
  return '#e63757';
};

const UnverifiedPrescriptionModal = ({
  show,
  onHide,
  initialFacilityId = 'ALL',
  initialBucket = 'ALL',
  facilityOptions = []
}) => {
  const [loading, setLoading] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState('diffDays');
  const [sortOrder, setSortOrder] = useState('desc');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [iframeSrc, setIframeSrc] = useState(null);

  const facilityLabel = useMemo(() => {
    if (initialFacilityId === 'ALL' || !initialFacilityId) {
      return 'All Facilities';
    }
    const match = facilityOptions.find(
      f => String(f.value) === String(initialFacilityId)
    );
    return match?.label || `Facility ${initialFacilityId}`;
  }, [initialFacilityId, facilityOptions]);

  const bucketInfo = BUCKET_CONFIG[initialBucket] || BUCKET_CONFIG.ALL;

  // Fetch and filter by selected bucket
  const fetchPrescriptions = useCallback(async (facilityId, bucketKey) => {
    setLoading(true);
    try {
      const payload = {
        facility_id:
          facilityId === 'ALL' || !facilityId ? '' : String(facilityId)
      };
      const response = await axios.post(
        '/reports/unverified-prescription-list',
        payload
      );
      const data = response?.data?.data || [];

      // Calculate diff days and filter strictly to the selected bucket and facility
      const formatted = data
        .map((item, idx) => {
          const diffDays = calculateDiffDays(item.created);
          return {
            ...item,
            uniqueKey: `${item.prescription_id || idx}-${idx}`,
            diffDays,
            fullDrugName: `${item.drug || ''} ${item.strength_value || ''} ${
              item.strength || ''
            }`.trim()
          };
        })
        .filter(item => {
          const matchesFacility =
            facilityId === 'ALL' ||
            !facilityId ||
            !item.facility_id ||
            String(item.facility_id) === String(facilityId);
          return matchesFacility && matchesBucket(item.diffDays, bucketKey);
        });

      setPrescriptions(formatted);
    } catch (error) {
      console.error('Error fetching unverified prescription list:', error);
      setPrescriptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      setSearchTerm('');
      setCurrentPage(1);
      setIframeSrc(null);
      fetchPrescriptions(initialFacilityId, initialBucket);
    }
  }, [show, initialFacilityId, initialBucket, fetchPrescriptions]);

  const getNoOfDays = useCallback(bucket => {
    if (!bucket || bucket === 'ALL') return null;
    const normalized = String(bucket).toLowerCase().trim();
    if (normalized === 'within_7' || normalized === 'within 7') return 'within 7';
    if (normalized === 'days_8_14' || normalized === '8-14' || normalized === '8–14') return '8-14';
    if (normalized === 'days_15_21' || normalized === '15-21' || normalized === '15–21') return '15-21';
    if (normalized === 'over_21' || normalized === '>21' || normalized === '> 21') return '>21';
    return null;
  }, []);

  const getReportPayload = useCallback(
    isPrintValue => {
      const payload = {
        is_print: isPrintValue
      };

      if (initialFacilityId && initialFacilityId !== 'ALL') {
        payload.facility_id = initialFacilityId;
      }

      const noOfDays = getNoOfDays(initialBucket);
      if (noOfDays) {
        payload.no_of_days = noOfDays;
      }

      return payload;
    },
    [initialFacilityId, initialBucket, getNoOfDays]
  );

  const handleDownloadReport = async () => {
    setDownloadLoading(true);
    try {
      const payload = getReportPayload(true);
      const response = await axios.post(
        '/reports/unverified-prescription-report',
        payload
      );

      if (response?.data?.status === false) {
        toast.error(response.data.message || 'Failed to generate report');
      } else {
        toast.success('Report Generated Successfully');
        const { blobUrl } = await createIframeReportBlobUrl(response.data);
        setIframeSrc(blobUrl);
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(
        error?.response?.data?.message || 'Failed to generate report'
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const payload = getReportPayload('csv');
      const response = await axios.post(
        '/reports/unverified-prescription-report',
        payload
      );

      if (response?.data?.status === false) {
        toast.error(response.data.message || 'Failed to export CSV');
      } else {
        const disposition = response?.headers
          ? response.headers['content-disposition']
          : null;
        const filename = disposition
          ? disposition.split('filename=')[1]?.replace(/"/g, '').trim()
          : 'Unverified-prescriptions-report.csv';

        const url = URL.createObjectURL(
          new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        link.parentNode.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Report Exported Successfully');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error(error?.response?.data?.message || 'Failed to export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const isExportDisabled =
    loading ||
    downloadLoading ||
    exportLoading ||
    prescriptions.length === 0;

  // Search filter
  const filteredData = useMemo(() => {
    let list = [...prescriptions];

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter(item => {
        return (
          (item.patient_name && item.patient_name.toLowerCase().includes(q)) ||
          (item.drug && item.drug.toLowerCase().includes(q)) ||
          (item.generic && item.generic.toLowerCase().includes(q)) ||
          (item.pharmacy_order_id &&
            String(item.pharmacy_order_id).toLowerCase().includes(q)) ||
          (item.facility_name && item.facility_name.toLowerCase().includes(q)) ||
          (item.fullDrugName && item.fullDrugName.toLowerCase().includes(q))
        );
      });
    }

    list.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (valA === null || valA === undefined) valA = '';
      if (valB === null || valB === undefined) valB = '';

      if (typeof valA === 'string') {
        return sortOrder === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      return sortOrder === 'asc' ? valA - valB : valB - valA;
    });

    return list;
  }, [prescriptions, searchTerm, sortField, sortOrder]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedData = useMemo(() => {
    const start = (currentPageSafe - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPageSafe, pageSize]);

  const handleSort = field => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <div style={{ height: 0, overflow: 'hidden', width: 0 }}>
        {iframeSrc && (
          <iframe
            src={iframeSrc}
            style={{ width: '100%', height: '400px', border: 'none' }}
            title="Preview Content"
          />
        )}
      </div>
      <Modal.Header closeButton className="bg-light py-2.5 px-3">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <FontAwesomeIcon icon="prescription" className="text-primary fs-5" />
          <h5 className="mb-0 text-900 fw-bold">Unverified Prescriptions</h5>
          <Badge
            pill
            className="fs-11 py-1 px-2 text-white"
            style={{ backgroundColor: bucketInfo.color }}
          >
            {bucketInfo.label}
          </Badge>
          <Badge pill bg="secondary" className="fs-11 py-1 px-2">
            {facilityLabel}
          </Badge>
          <Badge pill bg="light" className="text-dark border fs-11 py-1 px-2">
            {filteredData.length} Prescriptions
          </Badge>
        </div>
      </Modal.Header>

      <Modal.Body className="p-3">
        {/* Search bar & summary */}
        <Row className="g-2 mb-3 align-items-center justify-content-between">
          <Col xs={12} sm={7} md={8}>
            <div className="fs-11 text-700">
              Facility: <strong>{facilityLabel}</strong> | Filter:{' '}
              <strong style={{ color: bucketInfo.color }}>
                {bucketInfo.label}
              </strong>
            </div>
          </Col>

          <Col xs={12} sm={5} md={4}>
            <InputGroup size="sm">
              <InputGroup.Text className="bg-white border-end-0 text-400">
                <FontAwesomeIcon icon="search" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search patient, drug, order..."
                className="border-start-0 ps-0"
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="border-start-0 border-300"
                  onClick={() => setSearchTerm('')}
                >
                  <FontAwesomeIcon icon="times" />
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>

        {/* Table Content */}
        {loading ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center py-5"
            style={{ minHeight: '220px' }}
          >
            <Spinner animation="border" variant="primary" role="status" />
            <span className="mt-2 text-600 fs-11">
              Loading unverified prescriptions...
            </span>
          </div>
        ) : (
          <div
            className="border rounded-2"
            style={{ maxHeight: '450px', overflowY: 'auto' }}
          >
            <Table hover responsive className="fs-11 align-middle mb-0">
              <thead className="bg-200 text-800 position-sticky top-0 z-index-1 shadow-sm">
                <tr>
                  {/* <th className="py-2 px-3 text-center" style={{ width: '40px' }}>
                    #
                  </th> */}
                  <th
                    className="py-2 cursor-pointer"
                    onClick={() => handleSort('patient_name')}
                  >
                    Patient Name{' '}
                    {sortField === 'patient_name' && (
                      <FontAwesomeIcon
                        icon={sortOrder === 'asc' ? 'caret-up' : 'caret-down'}
                        className="ms-1 text-primary"
                      />
                    )}
                  </th>
                  <th
                    className="py-2 cursor-pointer"
                    onClick={() => handleSort('facility_name')}
                  >
                    Facility{' '}
                    {sortField === 'facility_name' && (
                      <FontAwesomeIcon
                        icon={sortOrder === 'asc' ? 'caret-up' : 'caret-down'}
                        className="ms-1 text-primary"
                      />
                    )}
                  </th>
                  <th
                    className="py-2 cursor-pointer"
                    onClick={() => handleSort('drug')}
                  >
                    Drug{' '}
                    {sortField === 'drug' && (
                      <FontAwesomeIcon
                        icon={sortOrder === 'asc' ? 'caret-up' : 'caret-down'}
                        className="ms-1 text-primary"
                      />
                    )}
                  </th>
                  {/* <th className="py-2">Generic</th> */}
                  {/* <th className="py-2 text-center">Order ID</th> */}
                  <th
                    className="py-2 text-center cursor-pointer"
                    onClick={() => handleSort('created')}
                  >
                    Created Date{' '}
                    {sortField === 'created' && (
                      <FontAwesomeIcon
                        icon={sortOrder === 'asc' ? 'caret-up' : 'caret-down'}
                        className="ms-1 text-primary"
                      />
                    )}
                  </th>
                  <th
                    className="py-2 text-center cursor-pointer"
                    onClick={() => handleSort('diffDays')}
                  >
                    Days Unverified{' '}
                    {sortField === 'diffDays' && (
                      <FontAwesomeIcon
                        icon={sortOrder === 'asc' ? 'caret-up' : 'caret-down'}
                        className="ms-1 text-primary"
                      />
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-5 text-500">
                      <FontAwesomeIcon
                        icon="info-circle"
                        className="text-400 fs-3 mb-2 d-block mx-auto"
                      />
                      No prescriptions found matching this filter.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => {
                    const rowNumber =
                      (currentPageSafe - 1) * pageSize + index + 1;
                    const badgeBg = getAgeBadgeColor(item.diffDays);
                    return (
                      <tr key={item.uniqueKey}>
                        {/* <td className="py-2 px-3 text-center text-500">
                          {rowNumber}
                        </td> */}
                        <td className="py-2 fw-semibold">
                          {item.patient_id ? (
                            <Link
                              to={`${paths.patientDetails}/${item.patient_id}`}
                              className="text-primary text-decoration-none hover-underline fw-bold"
                            >
                              {item.patient_name || '-'}
                            </Link>
                          ) : (
                            item.patient_name || '-'
                          )}
                        </td>
                        <td className="py-2 text-700">
                          {item.facility_name || '-'}
                        </td>
                        <td className="py-2">
                          {item.prescription_id ? (
                            <Link
                              to={`${paths.editPrescription}/${item.prescription_id}`}
                              className="text-900 text-decoration-none hover-underline fw-bold"
                            >
                              {item.fullDrugName || item.drug || '-'}
                            </Link>
                          ) : (
                            <span className="fw-semibold">
                              {item.fullDrugName || item.drug || '-'}
                            </span>
                          )}
                        </td>
                        {/* <td className="py-2 text-600">
                          {item.generic || '-'}
                        </td> */}
                        {/* <td className="py-2 text-center text-800 font-monospace">
                          {item.pharmacy_order_id ? (
                            <Badge bg="100" className="text-800 border">
                              {item.pharmacy_order_id}
                            </Badge>
                          ) : (
                            '-'
                          )}
                        </td> */}
                        <td className="py-2 text-center text-700">
                          {item.created || '-'}
                        </td>
                        <td className="py-2 text-center">
                          {/* <Badge
                            pill
                            className="fs-11 py-1 px-2 text-black"
                            style={{ backgroundColor: badgeBg }}
                          > */}
                            {item.diffDays !== null
                              ? `${item.diffDays} day${
                                  item.diffDays === 1 ? '' : 's'
                                }`
                              : '-'}
                          {/* </Badge> */}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light py-2 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3 fs-11 text-600">
          <span>
            Showing{' '}
            <strong>
              {filteredData.length === 0
                ? 0
                : (currentPageSafe - 1) * pageSize + 1}
            </strong>{' '}
            to{' '}
            <strong>
              {Math.min(currentPageSafe * pageSize, filteredData.length)}
            </strong>{' '}
            of <strong>{filteredData.length}</strong> prescriptions
          </span>

          <div className="d-flex align-items-center gap-1">
            <span>Per page:</span>
            <Form.Select
              size="sm"
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{ width: '65px', fontSize: '11px', padding: '2px 6px' }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {totalPages > 1 && (
            <Pagination size="sm" className="mb-0">
              <Pagination.Prev
                disabled={currentPageSafe <= 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              />
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPageSafe - 1 &&
                    pageNum <= currentPageSafe + 1)
                ) {
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === currentPageSafe}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                }
                if (
                  pageNum === currentPageSafe - 2 ||
                  pageNum === currentPageSafe + 2
                ) {
                  return <Pagination.Ellipsis key={pageNum} disabled />;
                }
                return null;
              })}
              <Pagination.Next
                disabled={currentPageSafe >= totalPages}
                onClick={() =>
                  setCurrentPage(prev => Math.min(totalPages, prev + 1))
                }
              />
            </Pagination>
          )}

          <Button
            variant="primary"
            size="sm"
            onClick={handleDownloadReport}
            disabled={isExportDisabled}
            className="d-flex align-items-center gap-1"
          >
            {downloadLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="download" />
                <span>Download Report</span>
              </>
            )}
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={handleExportCSV}
            disabled={isExportDisabled}
            className="d-flex align-items-center gap-1"
          >
            {exportLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="file-download" />
                <span>Export to CSV</span>
              </>
            )}
          </Button>

          <Button variant="secondary" size="sm" onClick={onHide}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

UnverifiedPrescriptionModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  initialFacilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialBucket: PropTypes.string,
  facilityOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string
    })
  )
};

export default UnverifiedPrescriptionModal;
